import { useEffect, useRef, useState } from "react";
import { RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";
import { Link, useNavigate } from "react-router-dom";

const TAB_VARIANTS = {
  primary: {
    base: "border-x-grey-border flex translate-y-px items-center border-x px-4 py-2",
    active: "border-blue-france text-blue-france hover:bg-gray-975 border-t-2 bg-white",
    inactive: "bg-blue-france-925 hover:bg-blue-france-925-hover border-0",
  },
  underline: {
    base: "pb-1",
    active: "border-blue-france text-blue-france border-b-2 font-semibold",
    inactive: "",
  },
};

export const Tab = ({ tab, panelId, isFocusable, onKeyDown, setRef, variant = "primary", className = "" }) => {
  const variantClasses = TAB_VARIANTS[variant] || TAB_VARIANTS.primary;
  // cursor-pointer : Tailwind 4 remet les <button> en `cursor: default` (les onglets `to` sont des liens, déjà en pointer)
  const tabClassName = `cursor-pointer whitespace-nowrap ${variantClasses.base} ${tab.isActive ? variantClasses.active : variantClasses.inactive} ${className}`.trim();
  const sharedProps = {
    id: tab.id,
    role: "tab",
    "aria-selected": tab.isActive,
    "aria-controls": panelId,
    tabIndex: isFocusable ? 0 : -1,
    ref: setRef,
    onKeyDown,
    className: tabClassName,
  };

  if (tab.to) {
    return (
      <Link key={tab.key} to={tab.to} {...sharedProps}>
        {tab.label}
      </Link>
    );
  }

  return (
    <button key={tab.key} type="button" onClick={tab.onSelect} {...sharedProps}>
      {tab.label}
    </button>
  );
};

const Tabs = ({ tabs, ariaLabel, panelId, className = "", variant = "primary", tabClassName = "" }) => {
  const navigate = useNavigate();
  const tabRefs = useRef([]);
  const listRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const activeTabIndex = tabs.findIndex((tab) => tab.isActive);
  const focusableTabIndex = activeTabIndex === -1 ? 0 : activeTabIndex;

  useEffect(() => {
    const checkOverflow = () => {
      const list = listRef.current;
      setIsOverflowing(list ? list.scrollWidth > list.clientWidth + 1 : false);
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [tabs]);

  if (!tabs?.length) {
    return null;
  }

  const handleScroll = (direction) => {
    listRef.current?.scrollBy({ left: direction * 150, behavior: "smooth" });
  };

  const focusTab = (index) => {
    const tab = tabRefs.current[index];
    if (tab) {
      tab.focus();
    }
  };

  const activateTab = (tab) => {
    if (tab.onSelect) {
      tab.onSelect();
    }
    if (tab.to) {
      navigate(tab.to);
    }
  };

  const handleTabKeyDown = (event, index, tab) => {
    switch (event.key) {
      case "ArrowRight": {
        event.preventDefault();
        focusTab((index + 1) % tabs.length);
        break;
      }
      case "ArrowLeft": {
        event.preventDefault();
        focusTab((index - 1 + tabs.length) % tabs.length);
        break;
      }
      case "Home": {
        event.preventDefault();
        focusTab(0);
        break;
      }
      case "End": {
        event.preventDefault();
        focusTab(tabs.length - 1);
        break;
      }
      case "Enter":
      case " ":
      case "Spacebar": {
        event.preventDefault();
        activateTab(tab);
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className="flex items-center">
      {/* RGAA 13.10 : alternative au swipe quand les onglets débordent */}
      {isOverflowing && (
        <button type="button" className="focus shrink-0 p-1 hover:bg-gray-100" aria-label="Faire défiler les onglets vers la gauche" onClick={() => handleScroll(-1)}>
          <RiArrowLeftSLine className="text-xl" aria-hidden="true" />
        </button>
      )}
      <div role="tablist" aria-label={ariaLabel} ref={listRef} className={`flex flex-nowrap overflow-x-auto overflow-y-hidden ${className}`}>
        {tabs.map((tab, index) => (
          <Tab
            key={tab.key}
            tab={tab}
            panelId={panelId}
            isFocusable={index === focusableTabIndex}
            setRef={(node) => {
              tabRefs.current[index] = node;
            }}
            onKeyDown={(event) => handleTabKeyDown(event, index, tab)}
            variant={variant}
            className={tabClassName}
          />
        ))}
      </div>
      {isOverflowing && (
        <button type="button" className="focus shrink-0 p-1 hover:bg-gray-100" aria-label="Faire défiler les onglets vers la droite" onClick={() => handleScroll(1)}>
          <RiArrowRightSLine className="text-xl" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default Tabs;
